import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperAirplaneIcon, SparklesIcon, TrashIcon, PlusIcon, PencilIcon, CheckIcon } from '@heroicons/react/24/solid';
import {
  IChatMessage,
  ChatContextType,
  ChatMessageRole,
  LLMStreamEventType,
  OutlineActionCard,
  SubjectActionType,
  OutlineActionTypeLabels,
  OutlineAIResponse,
  getLabelsByLocale,
  Locale
} from '@packages/global';
import { chatMessageApi, llmApi } from '../../services/user';
import { episodeOutlineApi } from '../../services/user/episodeOutline.api';
import { useToast } from '../../stores/toastStore';
import { useDialog } from '../../hooks/useDialog';

interface OutlineChatPanelProps {
  projectId: string;
  onOutlinesChange: () => void;  // 当大纲列表变化时回调
}

export default function OutlineChatPanel({
  projectId,
  onOutlinesChange
}: OutlineChatPanelProps) {
  const { t, i18n } = useTranslation(['projectSettings', 'common']);
  const outlineActionTypeLabels = getLabelsByLocale(OutlineActionTypeLabels, i18n.language as Locale);
  const toast = useToast();
  const { confirm } = useDialog();
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    loadMessages();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [projectId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  };

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await chatMessageApi.list({
        projectId,
        contextType: ChatContextType.EPISODE_OUTLINE,
        limit: 50
      });
      setMessages(data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleSend = async () => {
    if (!input.trim() || streaming) return;

    const userMessage = input.trim();
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setStreaming(true);
    setStreamingContent('');

    // 乐观更新：立即显示用户消息
    const tempUserMsg: IChatMessage = {
      id: `temp-${Date.now()}`,
      projectId,
      contextType: ChatContextType.EPISODE_OUTLINE,
      role: ChatMessageRole.USER,
      content: userMessage,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      abortControllerRef.current = new AbortController();

      // 不传 currentContent，让后端自动获取完整上下文
      await llmApi.stream(
        {
          projectId,
          contextType: ChatContextType.EPISODE_OUTLINE,
          prompt: userMessage
        },
        (event) => {
          switch (event.type) {
            case LLMStreamEventType.START:
              break;
            case LLMStreamEventType.DELTA:
              setStreamingContent(prev => prev + (event.content || ''));
              break;
            case LLMStreamEventType.DONE:
              // 尝试解析结构化输出
              const fullContent = event.content || '';
              let parsedResponse: OutlineAIResponse | null = null;

              try {
                // 尝试从内容中提取 JSON
                const jsonMatch = fullContent.match(/```json\s*([\s\S]*?)\s*```/);
                if (jsonMatch) {
                  parsedResponse = JSON.parse(jsonMatch[1]);
                } else {
                  // 尝试直接解析
                  parsedResponse = JSON.parse(fullContent);
                }
              } catch (e) {
                // 非结构化输出，作为普通文本处理
                parsedResponse = { message: fullContent, actions: [] };
              }

              // 添加 AI 消息
              const aiMsg: IChatMessage = {
                id: `ai-${Date.now()}`,
                projectId,
                contextType: ChatContextType.EPISODE_OUTLINE,
                role: ChatMessageRole.ASSISTANT,
                content: fullContent,
                createdAt: new Date(),
                updatedAt: new Date()
              };
              setMessages(prev => [...prev, aiMsg]);

              setStreamingContent('');
              setStreaming(false);
              break;
            case LLMStreamEventType.ERROR:
              toast.error(event.error || t('outlineChat.aiGenerateFailed'));
              setStreaming(false);
              setStreamingContent('');
              break;
          }
        },
        abortControllerRef.current.signal
      );
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Failed to send message:', error);
        toast.error(t('outlineChat.sendFailed'));
      }
      setStreaming(false);
      setStreamingContent('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = async () => {
    const confirmed = await confirm(t('outlineChat.clearConfirm'), t('outlineChat.clearConfirmTitle'), 'warning');
    if (!confirmed) return;
    try {
      await chatMessageApi.clear(projectId, ChatContextType.EPISODE_OUTLINE);
      setMessages([]);
      toast.success(t('outlineChat.chatCleared'));
    } catch (error) {
      console.error('Failed to clear chat history:', error);
      toast.error(t('outlineChat.clearFailed'));
    }
  };

  // 删除单条消息
  const handleDeleteMessage = async (messageId: string) => {
    // 临时消息直接从本地删除
    if (messageId.startsWith('temp-') || messageId.startsWith('ai-')) {
      setMessages(prev => prev.filter(m => m.id !== messageId));
      return;
    }
    try {
      await chatMessageApi.delete(messageId);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success(t('outlineChat.messageDeleted'));
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error(t('outlineChat.deleteFailed'));
    }
  };


  // 应用操作卡片
  const handleApplyAction = async (action: OutlineActionCard) => {
    try {
      switch (action.actionType) {
        case SubjectActionType.CREATE:
          await episodeOutlineApi.create({
            projectId,
            episodeNumber: action.episodeNumber,
            title: action.title,
            synopsis: action.synopsis
          });
          toast.success(t('outlineChat.episodeAdded', { number: action.episodeNumber }));
          break;

        case SubjectActionType.UPDATE:
          if (!action.outlineId) {
            toast.error(t('outlineChat.missingIdUpdate'));
            return;
          }
          await episodeOutlineApi.update(action.outlineId, {
            episodeNumber: action.episodeNumber,
            title: action.title,
            synopsis: action.synopsis
          });
          toast.success(t('outlineChat.episodeUpdated', { number: action.episodeNumber }));
          break;

        case SubjectActionType.DELETE:
          if (!action.outlineId) {
            toast.error(t('outlineChat.missingIdDelete'));
            return;
          }
          await episodeOutlineApi.delete(action.outlineId);
          toast.success(t('outlineChat.episodeDeleted', { number: action.episodeNumber }));
          break;
      }

      // 刷新大纲列表
      onOutlinesChange();
    } catch (error) {
      console.error('Failed to apply action:', error);
      toast.error(t('outlineChat.actionFailed'));
    }
  };

  // 获取操作按钮配置
  const getActionButtonConfig = (actionType: SubjectActionType) => {
    switch (actionType) {
      case SubjectActionType.CREATE:
        return { icon: PlusIcon, text: t('outlineChat.addEpisode'), color: '#22C55E' };
      case SubjectActionType.UPDATE:
        return { icon: PencilIcon, text: t('outlineChat.applyUpdate'), color: '#2563EB' };
      case SubjectActionType.DELETE:
        return { icon: TrashIcon, text: t('outlineChat.deleteEpisode'), color: '#EF4444' };
    }
  };

  // 格式化时间
  const formatTime = (date: Date) => {
    const d = new Date(date);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  // 解析消息内容，提取文本和操作卡片，兼容多种格式
  const parseMessageContent = (content: string): { text: string; actions: OutlineActionCard[] } => {
    try {
      // 1. 尝试匹配 markdown 代码块中的 JSON
      const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[1]) as OutlineAIResponse;
        return { text: parsed.message, actions: parsed.actions || [] };
      }

      // 2. 尝试直接解析为 JSON
      const trimmed = content.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        const parsed = JSON.parse(trimmed) as OutlineAIResponse;
        return { text: parsed.message, actions: parsed.actions || [] };
      }

      // 3. 尝试提取 JSON 对象
      const jsonObjectMatch = content.match(/\{[\s\S]*"message"[\s\S]*\}/);
      if (jsonObjectMatch) {
        const parsed = JSON.parse(jsonObjectMatch[0]) as OutlineAIResponse;
        return { text: parsed.message, actions: parsed.actions || [] };
      }

      return { text: content, actions: [] };
    } catch {
      return { text: content, actions: [] };
    }
  };


  return (
    <div className="h-full flex flex-col bg-bg-page rounded-xl border border-border-subtle overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center">
            <SparklesIcon className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-gray-50">{t('outlineChat.title')}</span>
        </div>
        <div className="flex items-center gap-2">
          {messages.length > 0 && (
            <button
              onClick={handleClear}
              className="p-1.5 text-gray-400 hover:text-error transition-colors cursor-pointer"
              title={t('outlineChat.clearChat')}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
          <div className="flex items-center gap-1.5 px-2 py-1 bg-success/10 rounded">
            <div className="w-1.5 h-1.5 bg-success rounded-full" />
            <span className="text-[11px] text-success font-medium">{t('outlineChat.online')}</span>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400">
            <span className="text-sm">{t('outlineChat.loading')}</span>
          </div>
        ) : (
          <>
            {/* 默认欢迎消息 - 始终显示 */}
            <div className="flex gap-2.5">
              <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                <SparklesIcon className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="bg-bg-elevated rounded-xl px-3 py-2.5 max-w-[85%]">
                <p className="text-[13px] text-gray-200 leading-relaxed">
                  {t('outlineChat.welcome')}
                </p>
              </div>
            </div>
            {messages.map((msg) => {
              const isUser = msg.role === ChatMessageRole.USER;
              const { text, actions } = isUser ? { text: msg.content, actions: [] } : parseMessageContent(msg.content);

              return (
                <div key={msg.id} className="flex flex-col gap-2">
                  <div className={`group flex gap-2.5 ${isUser ? 'justify-end' : ''}`}>
                    {/* AI Avatar */}
                    {!isUser && (
                      <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                        <SparklesIcon className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}

                    {/* Message Bubble */}
                    <div className={`flex flex-col gap-1 max-w-[85%] ${isUser ? 'items-end' : ''}`}>
                      <div
                        className={`rounded-xl px-3 py-2.5 ${isUser
                          ? 'bg-primary/10 border border-primary'
                          : 'bg-bg-elevated'
                          }`}
                      >
                        <p
                          className={`text-[13px] leading-relaxed whitespace-pre-wrap break-words ${isUser ? 'text-primary' : 'text-gray-200'
                            }`}
                        >
                          {text.length > 500 ? text.slice(0, 500) + '...' : text}
                        </p>
                      </div>
                      {/* 时间和删除按钮 */}
                      <div className={`flex items-center gap-2 ${isUser ? 'justify-end' : ''}`}>
                        {!isUser && (
                          <button
                            onClick={() => handleDeleteMessage(msg.id)}
                            className="p-0.5 text-gray-500 hover:text-error transition-colors cursor-pointer"
                            title={t('outlineChat.deleteMessage')}
                          >
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        )}
                        <span className={`text-[11px] ${isUser ? 'text-primary/50' : 'text-gray-400'}`}>
                          {formatTime(msg.createdAt)}
                        </span>
                        {isUser && (
                          <button
                            onClick={() => handleDeleteMessage(msg.id)}
                            className="p-0.5 text-gray-500 hover:text-error transition-colors cursor-pointer"
                            title={t('outlineChat.deleteMessage')}
                          >
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Cards for this message */}
                  {!isUser && actions.length > 0 && (
                    <div className="flex flex-col gap-2 ml-9">
                      {actions.map((action, idx) => {
                        const config = getActionButtonConfig(action.actionType);
                        const Icon = config.icon;
                        return (
                          <div
                            key={idx}
                            className="rounded-lg border p-3 bg-bg-surface border-border-default"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span
                                  className="px-2 py-0.5 text-[11px] font-semibold text-white rounded"
                                  style={{ backgroundColor: config.color }}
                                >
                                  {outlineActionTypeLabels[action.actionType]}
                                </span>
                                <span className="text-[13px] font-semibold text-gray-50">
                                  {t('outlineChat.episodeLabel', { number: action.episodeNumber })}
                                </span>
                              </div>
                              <button
                                onClick={() => handleApplyAction(action)}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors hover:bg-white/5"
                                style={{ borderColor: config.color, color: config.color }}
                              >
                                <Icon className="w-3 h-3" />
                                {config.text}
                              </button>
                            </div>
                            <p className="text-[12px] leading-relaxed line-clamp-3 text-gray-300">
                              {action.title}：{action.synopsis}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}


            {/* Streaming Message */}
            {streaming && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                  <SparklesIcon className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-bg-elevated rounded-xl px-3 py-2.5 max-w-[85%]">
                  {streamingContent ? (
                    <p className="text-[13px] text-gray-200 leading-relaxed whitespace-pre-wrap break-words">
                      {streamingContent}
                    </p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span className="text-[13px] text-gray-400">{t('outlineChat.thinking')}</span>
                    </div>
                  )}
                  {streamingContent && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
                      <span className="text-[11px] text-gray-400">{t('outlineChat.generating')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="flex items-end gap-3 p-4 border-t border-border-subtle flex-shrink-0">
        <div className="flex-1">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={handleKeyDown}
            placeholder={t('outlineChat.inputPlaceholder')}
            className="w-full bg-bg-elevated text-gray-50 text-[13px] rounded-lg px-3.5 py-2.5 resize-none focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-gray-400 overflow-y-auto"
            rows={1}
            disabled={streaming}
          />
        </div>
        <button
          onClick={handleSend}
          disabled={!input.trim() || streaming}
          className="w-10 h-10 bg-primary hover:bg-primary/90 disabled:bg-bg-elevated disabled:text-gray-400 text-white rounded-lg flex items-center justify-center transition-colors flex-shrink-0 cursor-pointer disabled:cursor-not-allowed"
        >
          <PaperAirplaneIcon className="w-[18px] h-[18px]" />
        </button>
      </div>
    </div>
  );
}
